import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useAlert, useConfirmationAlert } from '@euler/app/tv/components/Alert';
import { EmptyPlaceholder } from '@euler/app/tv/components/EmptyPlaceholder';
import {
  CustomMaintenancePlanModal,
  CustomMaintenancePlanParams,
  useCustomMaintenancePlanModal,
} from '@euler/app/tv/components/maintenance-plan/CustomMaintenancePlanModal';
import { MaintenancePlanCell } from '@euler/app/tv/components/maintenance-plan/MaintenancePlanCell';
import {
  MaintenancePlanItem,
  MaintenancePlanStartParams,
  MaintenancePlanValueParams,
} from '@euler/app/tv/components/maintenance-plan/types';
import { TaskScreenContent } from '@euler/app/tv/components/TaskScreenContent';
import { useScreenContentPadding } from '@euler/app/tv/functions/utils';
import type { TaskNavParams } from '@euler/app/tv/pages/TaskFlow';
import { useAppLoading } from '@euler/components/loading/Provider';
import {
  defectiveLevelDistributed,
  getDefectiveLevelFrom,
} from '@euler/functions/getDefectiveLevel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { DefectiveLevel } from '@euler/model';
import {
  AbnormalLevel,
  InspectionTaskJobStatus,
  SeverityLevel,
} from '@euler/model/enum';
import { TaskDetail } from '@euler/model/task-detail';
import { delayed } from '@euler/utils';
import { dateFromValue } from '@euler/utils/datetime';
import { formatDate } from '@euler/utils/formatters';
import { usePersistFn } from '@euler/utils/hooks';
import { vin_decode } from '@euler/utils/vin/parser';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import addMonths from 'date-fns/addMonths';
import differenceInMonths from 'date-fns/differenceInMonths';
import getYear from 'date-fns/getYear';
import { useCallback, useMemo } from 'react';
import { FlatList, ListRenderItem, Platform } from 'react-native';
import { Subject } from 'rxjs';

export const MaintenancePlanScreen = wrapNavigatorScreen(() => {
  const { rpx } = useResponsiveContext();
  const navigation = useNavigation<StackNavigationProp<TaskNavParams>>();
  const { detail, taskManager } = useTaskContext();
  const [paddingLeft, paddingRight, insets] = useScreenContentPadding();
  const loading = useAppLoading();
  const confirmAsync = useConfirmationAlert();
  const alertAsync = useAlert();

  const startParams = useMemo<MaintenancePlanStartParams>(() => {
    const decoded = vin_decode(detail.basicInfo.vin);
    const currentDate = dateFromValue(detail.basicInfo.createdAt, true);
    return {
      startDate: currentDate,
      currentVehicleMileage: detail.basicInfo.vehicleMileage,
      currentVehicleAge: Math.max(
        1,
        getYear(currentDate) - decoded.production_year,
      ),
    };
  }, [
    detail.basicInfo.createdAt,
    detail.basicInfo.vehicleMileage,
    detail.basicInfo.vin,
  ]);

  const items = useMaintenancePlanItems(detail, startParams);

  const save = usePersistFn(
    async (
      item: MaintenancePlanItem,
      value: MaintenancePlanValueParams,
      shouldConfirm: boolean,
    ) => {
      const { abnormalLevel, severityLevel } =
        item.defectiveLevel != null
          ? defectiveLevelDistributed(item.defectiveLevel)
          : {
              abnormalLevel: AbnormalLevel.Urgent,
              severityLevel: SeverityLevel.Danger,
            };
      const offset = differenceInMonths(value.date, startParams.startDate);
      if (offset === 0) {
        // create construction job
        if (
          shouldConfirm &&
          !(await confirmAsync({
            title: '确认保养',
            message:
              '您选择了在本次进行维修保养，我们将会您计划施工项目并安排施工，是否确认?',
            confirmButtonText: '确认进行本次保养',
            cancelButtonText: '稍后再说',
          }))
        ) {
          return;
        }
        await loading.showWithAction(async () => {
          await taskManager.constructionManager.addConstructionJob({
            name: item.name,
          });
        });
      } else {
        // create maintenance plan
        if (
          shouldConfirm &&
          !(await confirmAsync({
            title: '确认预约保养',
            message:
              '您选择了在下次进行维修保养，我们将提前通知您前来保养，是否确认?',
            confirmButtonText: '确认预约保养',
            cancelButtonText: '残忍放弃',
          }))
        ) {
          return;
        }
        const nextServiceDate = addMonths(startParams.startDate, offset);
        await loading.showWithAction(async () => {
          await taskManager.maintenancePlanManager.saveMaintenancePlanDetail({
            name: item.name,
            abnormalLevel,
            severityLevel,
            nextServiceDate,
            nextServiceMileage: value.mileage,
          });
        });
      }
    },
  );

  const onSave = usePersistFn(
    async (item: MaintenancePlanItem, value: MaintenancePlanValueParams) => {
      await save(item, value, true);
    },
  );

  const {
    show: showCustomMaintenancePlanModal,
    hide: hideCustomMaintenancePlanModal,
    initialState: initialCustomMaintenancePlanParams,
  } = useCustomMaintenancePlanModal();

  const onAddCustomMaintenancePlan = useCallback(
    (
      item: MaintenancePlanItem,
      params: MaintenancePlanValueParams,
      confirmed$: Subject<MaintenancePlanValueParams | undefined>,
    ) => {
      showCustomMaintenancePlanModal({ item, params, confirmed$ });
    },
    [showCustomMaintenancePlanModal],
  );

  const onDelete = useCallback(
    async (
      item: MaintenancePlanItem,
      confirmed$: Subject<MaintenancePlanValueParams | undefined>,
    ) => {
      // const offset = differenceInMonths(item.date, startParams.startDate);
      if (item.planDetail != null) {
        const { id, nextServiceDate, nextServiceMileage } = item.planDetail;
        if (
          !(await confirmAsync({
            title: '取消预约',
            message: `您即将取消于${formatDate(
              nextServiceDate,
              'yyyy年MM月dd日',
            )}约${nextServiceMileage}公里时的保养预约，取消预约保养后，请密切关注爱车使用情况及时到店保养，是否确认?`,
            confirmButtonText: '确认取消',
            cancelButtonText: '保留预约',
          }))
        ) {
          return;
        }
        await loading.showWithAction(async () => {
          await taskManager.maintenancePlanManager.deleteMaintenancePlanDetail(
            id,
          );
          void alertAsync({ title: '提示', message: '已取消预约保养项目！' });
          confirmed$.next(undefined);
        });
      } else if (item.constructionJob != null) {
        const job = item.constructionJob;
        if (job.status !== InspectionTaskJobStatus.Pending) {
          await alertAsync({
            title: '提示',
            message:
              job.status === InspectionTaskJobStatus.InProgress
                ? '该项目正在施工，请在技师端进行取消！'
                : '该项目已经完成施工，不能取消！',
          });
          return;
        }
        if (
          !(await confirmAsync({
            title: '取消施工计划',
            message: `您即将取消于针对该“${item.name}”项目的施工计划，是否确认取消?`,
            confirmButtonText: '确认取消',
            cancelButtonText: '保留计划',
          }))
        ) {
          return;
        }
        await loading.showWithAction(async () => {
          await taskManager.constructionManager.cancelConstructionJob(job.id);
        });
        await alertAsync({ title: '提示', message: '已取消施工项目！' });
      }
    },
    [
      alertAsync,
      confirmAsync,
      loading,
      taskManager.constructionManager,
      taskManager.maintenancePlanManager,
    ],
  );

  const onViewInspectionResults = useCallback(
    (item: MaintenancePlanItem) => {
      navigation.navigate('_inspectedSiteDetail', {
        filter: {
          type: 'maintenance-advice',
          maintenanceAdvice: item.name,
        },
      });
    },
    [navigation],
  );

  const renderItem: ListRenderItem<MaintenancePlanItem> = useCallback(
    ({ item, index }) => {
      return (
        <MaintenancePlanCell
          item={item}
          index={index}
          onSave={onSave}
          onDelete={onDelete}
          onAddCustomMaintenancePlan={onAddCustomMaintenancePlan}
          onViewInspectionResults={onViewInspectionResults}
          css={`
            margin-bottom: ${rpx(20)}px;
          `}
        />
      );
    },
    [
      onAddCustomMaintenancePlan,
      onDelete,
      onSave,
      onViewInspectionResults,
      rpx,
    ],
  );

  const onSaveCustomMaintenancePlan = useCallback(
    async (values: CustomMaintenancePlanParams) => {
      values.confirmed$.next(values.params);
      hideCustomMaintenancePlanModal();
      delayed(async () => {
        await save(values.item, values.params, false);
      }, 500);
    },
    [hideCustomMaintenancePlanModal, save],
  );

  return (
    <TaskScreenContent
      css={`
        padding-bottom: 0;
      `}
    >
      {items.length === 0 ? (
        <EmptyPlaceholder
          text="暂无相关维保计划"
          iconSize={rpx(200)}
          css={`
            flex: 1;
          `}
        />
      ) : (
        <FlatList
          data={items}
          keyExtractor={x => x.name}
          renderItem={renderItem}
          showsVerticalScrollIndicator={Platform.OS === 'web'}
          initialNumToRender={6}
          contentContainerStyle={{
            paddingLeft,
            paddingRight,
            paddingBottom: insets.bottom,
          }}
        />
      )}
      <CustomMaintenancePlanModal
        initialState={initialCustomMaintenancePlanParams}
        startParams={startParams}
        onClose={hideCustomMaintenancePlanModal}
        onConfirm={onSaveCustomMaintenancePlan}
      />
    </TaskScreenContent>
  );
});

const useMaintenancePlanItems = (
  detail: TaskDetail,
  startParams: MaintenancePlanStartParams,
) => {
  return useMemo(() => {
    const res: MaintenancePlanItem[] = [];
    const itemMap = new Map<string, MaintenancePlanItem>();

    const getItem = (name: string) => {
      let item = itemMap.get(name);
      if (item == null) {
        item = {
          name,
          startParams,
        };
        itemMap.set(name, item);
        res.push(item);
      }
      return item;
    };

    for (const inspectedSite of [
      ...detail.preInspection.sites,
      ...detail.inspection.sites,
    ]) {
      for (const inspectedItem of inspectedSite.items) {
        const defectiveLevel = getDefectiveLevelFrom(inspectedItem);
        if (
          defectiveLevel === DefectiveLevel.Fine ||
          !inspectedItem.maintenanceAdvice
        ) {
          continue;
        }
        const item = getItem(inspectedItem.maintenanceAdvice);
        if (
          item.defectiveLevel == null ||
          defectiveLevel > item.defectiveLevel
        ) {
          item.defectiveLevel = defectiveLevel;
        }
      }
      for (const customIssue of inspectedSite.customIssues) {
        const defectiveLevel = getDefectiveLevelFrom(customIssue);
        if (defectiveLevel === DefectiveLevel.Fine) {
          continue;
        }
        const item = getItem(customIssue.maintenanceAdvice);
        if (
          item.defectiveLevel == null ||
          defectiveLevel > item.defectiveLevel
        ) {
          item.defectiveLevel = defectiveLevel;
        }
      }
    }

    for (const customIssue of [
      ...(detail.preInspection.customIssues ?? []),
      ...(detail.inspection.customIssues ?? []),
    ]) {
      const defectiveLevel = getDefectiveLevelFrom(customIssue);
      if (
        defectiveLevel === DefectiveLevel.Fine ||
        !customIssue.maintenanceAdvice
      ) {
        continue;
      }
      const item = getItem(customIssue.maintenanceAdvice);
      if (item.defectiveLevel == null || defectiveLevel > item.defectiveLevel) {
        item.defectiveLevel = defectiveLevel;
      }
    }

    for (const job of detail.construction.jobs) {
      const item = getItem(job.name);
      item.constructionJob = job;
    }

    if (detail.maintenancePlan.plan != null) {
      for (const planDetail of detail.maintenancePlan.plan.details) {
        const item = getItem(planDetail.name);
        item.planDetail = planDetail;
      }
    }

    return res;
  }, [
    detail.construction.jobs,
    detail.inspection.customIssues,
    detail.inspection.sites,
    detail.maintenancePlan.plan,
    detail.preInspection.customIssues,
    detail.preInspection.sites,
    startParams,
  ]);
};
